import { jwtDecode } from "jwt-decode";
import { authedFetch } from "../api/authedFetch";

const API_GATEWAY_URL = process.env.REACT_APP_API_GATEWAY_URL;

// accessToken now lives in an HttpOnly cookie set by limkc-auth on login/refresh.
// JS cannot read it (intentional — XSS exfiltration defence). Authenticated
// fetches must use `credentials: "include"` so the browser auto-attaches the
// cookie. Returning empty here so any leftover caller still resolves cleanly
// rather than throwing — they should be migrated off this getter entirely.
export const getToken = () => "";

export const loginWithOAuth = async (provider, token) => {
  try {
    if (!API_GATEWAY_URL) {
      console.error("🔴 Error: API_GATEWAY_URL is undefined.");
      throw new Error("API Gateway URL is undefined");
    }

    const response = await fetch(`${API_GATEWAY_URL}/oauth`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ provider, token }),
      credentials: "include",
    });

    const data = await response.json();
    if (!response.ok) {
      console.error("❌ OAuth Login Error:", data);
      throw new Error(data.message || "OAuth login failed");
    }

    // accessToken now lives in HttpOnly cookie (Set-Cookie from limkc-auth).
    // idToken kept in localStorage for jwtDecode user-claim display — separate
    // migration scope per /JWT_COOKIES_MIGRATION_PLAN.md.
    localStorage.setItem("idToken", data.data.idToken);
    localStorage.setItem("identityId", data.data.identityId || "");

    const decodedUser = jwtDecode(data.data.idToken);
    return { success: true, user: decodedUser };
  } catch (error) {
    console.error("🔴 OAuth Login error:", error);
    return { success: false, message: error.message };
  }
};

export const loginUser = async (email, password, turnstileToken) => {
  try {
    if (!API_GATEWAY_URL) {
      console.error("🔴 Error: API_GATEWAY_URL is undefined.");
      throw new Error("API Gateway URL is undefined");
    }

    const response = await fetch(`${API_GATEWAY_URL}/user_login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, turnstileToken }),
      credentials: "include",
    });

    const data = await response.json();
    // console.log("Raw login response from Lambda:", JSON.stringify(data, null, 2)); // Debug: Log the raw response
    if (!response.ok) {
      console.error("❌ Login Error:", data);
      throw new Error(data.message || "Login failed");
    }

    // accessToken now lives in HttpOnly cookie. idToken kept for user-claim display.
    localStorage.setItem("idToken", data.data.idToken);
    localStorage.setItem("identityId", data.data.identityId);

    const decodedUser = jwtDecode(data.data.idToken);
    const user = {
      ...decodedUser,
      email: decodedUser.email || email,
      name: decodedUser.name || data.data.name || "Unknown User",
      'custom:membership': data.data['custom:membership'] || 'standard',
      'custom:messagesUsed': data.data['custom:messagesUsed'] || 0,
      'custom:limit': data.data['custom:limit'] || 500,
      'custom:registrationDate': data.data['custom:registrationDate'] || new Date().toISOString().split('T')[0],
      'custom:resetDate': data.data['custom:resetDate'] || '2025-04-01',
      'custom:nextBillingDate': data.data['custom:nextBillingDate'] || '2025-04-01',
      'custom:accountStatus': data.data['custom:accountStatus'] || 'active',
      'custom:billingCycle': data.data['custom:billingCycle'] || 'Start of month',
      'custom:billingDay': data.data['custom:billingDay'] || 1,
      'custom:paymentMethod': data.data['custom:paymentMethod'] || 'Credit',
      'custom:trialEndDate': data.data['custom:trialEndDate'] || 'NA',
      'custom:lastPaymentDate': data.data['custom:lastPaymentDate'] || 'na',
      'custom:planHistory': data.data['custom:planHistory'] || 'na',
      'custom:voiceUsage_percent': data.data['custom:voiceUsage_percent'] || '0'
    };
    // console.log("Constructed user object:", user);
    return { success: true, user };
  } catch (error) {
    console.error("🔴 Login error:", error.message);
    return { success: false, message: error.message };
  }
};

export const refreshAccessToken = async () => {
  try {
    if (!API_GATEWAY_URL) {
      console.error("🔴 Error: API_GATEWAY_URL is undefined.");
      throw new Error("API Gateway URL is undefined");
    }

    const response = await authedFetch(`${API_GATEWAY_URL}/refresh_token`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error("Refresh failed:", errorData);
      throw new Error("Token refresh failed");
    }

    const data = await response.json();
    const accessToken = data.data.accessToken;
    if (!accessToken || typeof accessToken !== "string") {
      console.error("Invalid accessToken received:", accessToken, "Full data:", JSON.stringify(data, null, 2));
      throw new Error("Invalid access token in response");
    }

    // accessToken cookie was refreshed by limkc-auth. Persist the fresh idToken
    // so THIS origin establishes its session from the shared .limkc.com cookie —
    // this is what lets a login on limkc.com carry into Chatty on app.limkc.com
    // (cross-subdomain SSO: same refreshToken cookie authorized this refresh).
    if (data.data && data.data.idToken) {
      localStorage.setItem("idToken", data.data.idToken);
      localStorage.setItem("userLoggedIn", "true");
    }
    // Return value retained for any caller still expecting it.
    return accessToken;
  } catch (error) {
    console.error("🔴 Refresh token error:", error);
    logoutUser();
    return null;
  }
};

export const logoutUser = async () => {
  // accessToken now lives in HttpOnly cookie — JS cannot read it. limkc-auth
  // logout handler reads access_token from event.cookies (cookie-first /
  // body-fallback), so frontend just calls /user_logout with empty body and
  // credentials: "include". The handler returns Set-Cookie clearing both
  // refreshToken and access_token cookies (Max-Age=0).
  try {
    const response = await authedFetch(`${API_GATEWAY_URL}/user_logout`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      console.error("Logout API request failed:", await response.text());
    } else {
      console.log("Logged out.");
    }
  } catch (error) {
    if (error.message === 'Failed to fetch') {
      console.info("🔌 Logout API unavailable (network/connectivity). Local logout completed successfully.");
    } else {
      console.error("Error during logout API call:", error);
    }
  }

  // idToken / identityId still live in localStorage (out of cookie-migration
  // scope per /JWT_COOKIES_MIGRATION_PLAN.md). userLoggedIn is a UI flag.
  localStorage.removeItem("idToken");
  localStorage.removeItem("identityId");
  localStorage.removeItem("userLoggedIn");
};

export const registerUser = async (email, password, turnstileToken, attributes) => {
  try {
    if (!API_GATEWAY_URL) {
      console.error(" Error: API_GATEWAY_URL is undefined.");
      throw new Error("API Gateway URL is undefined");
    }

    const response = await fetch(`${API_GATEWAY_URL}/register`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email,
        password,
        turnstileToken,
        attributes, // Pass custom attributes to Lambda
      }),
    });

    const responseData = await response.json();
    if (!response.ok) {
      console.error("❌ AWS Cognito Registration Error:", responseData);
      throw new Error(responseData.message || "Registration failed");
    }

    if (responseData.UserConfirmed === false) {
      return {
        success: true,
        message: "Registration successful! A verification email has been sent.",
        needsVerification: true,
        email: email,
      };
    }

    return responseData;
  } catch (error) {
    console.error("⚠️ Registration error:", error);
    return { success: false, message: error.message };
  }
};

export const verifyUser = async (email, code) => {
  try {
    if (!API_GATEWAY_URL) {
      console.error("🔴 Error: API_GATEWAY_URL is undefined.");
      throw new Error("API Gateway URL is undefined");
    }

    const response = await fetch(`${API_GATEWAY_URL}/user_confirmed`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, confirmationCode: code }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Verification failed");
    }

    return await response.json();
  } catch (error) {
    console.error("❌ Verification error:", error);
    return { success: false, message: error.message };
  }
};

export const forgotPassword = async (email, turnstileToken) => {
  try {
    if (!API_GATEWAY_URL) {
      console.error("🔴 Error: API_GATEWAY_URL is undefined.");
      throw new Error("API Gateway URL is undefined");
    }

    const response = await fetch(`${API_GATEWAY_URL}/user_forgot_pass`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, turnstileToken }),
    });

    const data = await response.json();
    if (!response.ok) {
      console.error("❌ Forgot Password Error:", data.message);
      throw new Error(data.message || "Failed to send reset email.");
    }

    return { success: true, message: "Password reset email sent." };
  } catch (error) {
    console.error("⚠️ Forgot Password Request Failed:", error);
    return { success: false, message: error.message };
  }
};

export const resetPassword = async (email, verificationCode, newPassword, turnstileToken) => {
  try {
    if (!API_GATEWAY_URL) {
      console.error("🔴 Error: API_GATEWAY_URL is undefined.");
      throw new Error("API Gateway URL is undefined");
    }

    const response = await fetch(`${API_GATEWAY_URL}/user_reset_pass`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email,
        verificationCode,
        newPassword,
        turnstileToken,
      }),
    });

    const data = await response.json();
    if (!response.ok) {
      console.error("❌ Reset Password Error:", data.message);
      throw new Error(data.message || "Failed to reset password.");
    }

    return { success: true, message: "Password reset successful." };
  } catch (error) {
    const enhancedMessage = `Invalid Email AND/OR ${error.message}`;
    console.error("⚠️ Reset Password Request Failed:", enhancedMessage);
    return { success: false, message: error.message };
  }
};

export const changePassword = async (oldPassword, newPassword) => {
  try {
    if (!API_GATEWAY_URL) {
      console.error("🔴 Error: API_GATEWAY_URL is undefined.");
      throw new Error("API Gateway URL is undefined");
    }

    // accessToken auth carried via HttpOnly cookie auto-attached by browser
    // (credentials: "include"). limkc-auth reset_pass handler reads cookie
    // first, falls through to Authorization header for native clients.
    const response = await authedFetch(`${API_GATEWAY_URL}/user_reset_pass`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        previousPassword: oldPassword,
        proposedPassword: newPassword
      }),
    });

    const data = await response.json();
    if (!response.ok) {
      console.error("❌ Change Password Error:", data);
      throw new Error(data.message || "Failed to change password.");
    }

    return { success: true, message: "Password changed successfully." };
  } catch (error) {
    console.error("🔴 Change Password error:", error.message);
    return { success: false, message: error.message };
  }
};